import { ThemeCssUnit } from '@sites/site-theme/src/enums/css.enum';
import type { ThemeCssSize } from '../base';

/**
 * The suffixes a stored length can carry.
 *
 * Only the two the schema allows. A suffix outside them — `'1.5rem'`, say — is NOT read as its
 * number with the fallback unit: that would turn `1.5rem` into `1.5px` and call it a migration.
 * It returns nothing instead, and the caller decides what an unreadable length means.
 */
const UNIT_BY_SUFFIX: ReadonlyArray<readonly [string, ThemeCssUnit]> = [
    ['px', ThemeCssUnit.px],
    ['%', ThemeCssUnit.percent],
];

/** Any letters or `%` at the end of a stored length, whether or not we can read them. */
const SUFFIX_PATTERN = /[a-z%]+$/i;

/**
 * `'16px'`, `'140%'`, `'0'` or a bare `16` into `{ value, unit }`.
 *
 * Every length the pre-versioning theme stored went through one of those spellings, and which one
 * depended on which panel last wrote it: the settings forms stripped the unit to satisfy a numeric
 * input and re-appended it on save, so a theme saved mid-edit carries the bare number. `'0'` is the
 * same ambiguity at its sharpest — a length with no unit at all.
 *
 * `fallbackUnit` is what a unitless number has always meant for that property: px for sizes,
 * borders and radii, % for line-height. It is the only thing such a value can mean.
 *
 * Anything that is not a number and does not parse — a cleared `null`, an empty string, `false` —
 * returns `undefined` rather than a default. An absent length falls through to the browser's own,
 * whereas inventing a `0px` border would silently change how a page renders.
 */
export function toCssSize(raw: unknown, fallbackUnit: ThemeCssUnit): ThemeCssSize | undefined {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? { value: raw, unit: fallbackUnit } : undefined;
    }

    if (typeof raw !== 'string') {
        return undefined;
    }

    const trimmed = raw.trim();
    const match = UNIT_BY_SUFFIX.find(([suffix]) => trimmed.endsWith(suffix));
    const value = Number.parseFloat(trimmed);

    if (!Number.isFinite(value)) {
        return undefined;
    }

    // A suffix we cannot read is not the same as no suffix at all.
    if (!match && SUFFIX_PATTERN.test(trimmed)) {
        return undefined;
    }

    return { value, unit: match ? match[1] : fallbackUnit };
}
